// frontend/app/components/DavinciCertificate.js
//
// BDD 2026-06-10 — Da Vinci 3-Day Challenge certificate ("Celestial Honors").
//
// Premium award certificate: midnight-navy starfield frame, gold-foil display
// type (Cormorant Garamond), Pinyon Script names/signatures, cream parchment.
// The completed-subject 3D models are rendered live from the bundled .glb files,
// snapshotted to transparent PNGs at build time, and placed as gold-ringed
// medallions. The on-screen preview is the EXACT print HTML in an <iframe>, so
// what the operator sees is what prints.
//
// Web/kiosk only (needs WebGL + the model-viewer script + Google Fonts). The RN
// fallback (rare; kiosk is a browser) shows a simple text certificate.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet, Platform } from "react-native";

import {
  DAVINCI_LOGO,
  SARAS_LOGO,
  buildCertHtml,
  formatDate,
  resolveUri,
  subjectToModel,
} from "../utils/davinciCertHtml";
import { getModelImgsForSubjects } from "../utils/davinciModels";

export default function DavinciCertificate({
  studentName,
  subjects = [],
  completedAt = null,
  code = "",
  onClose,
  onHtmlReady,
}) {
  const dateStr = formatDate(completedAt);
  const isWeb = Platform.OS === "web" && typeof window !== "undefined";
  const name = studentName || "Student";

  const modelPlan = useMemo(() => {
    const list = Array.isArray(subjects) ? subjects.filter(Boolean) : [];
    const seen = new Set();
    const out = [];
    for (const subj of list) {
      const m = subjectToModel(subj);
      if (seen.has(m.key)) continue;
      seen.add(m.key);
      out.push(m);
    }
    return out;
  }, [subjects]);

  const previewRef = useRef(null);
  const modelImgsRef = useRef([]);
  const [certHtml, setCertHtml] = useState(null);

  const makeHtml = (modelImgs, autoPrint) =>
    buildCertHtml({
      studentName: name,
      dateStr,
      code,
      davinciUri: resolveUri(DAVINCI_LOGO),
      sarasUri: resolveUri(SARAS_LOGO),
      modelImgs,
      autoPrint,
    });

  // Build phase: snapshot the subject 3D models (cached + serialized in the
  // shared util) and assemble the HTML.
  useEffect(() => {
    if (!isWeb) return;
    let cancelled = false;

    (async () => {
      // getModelImgsForSubjects dedupes by model key, captures one model at a
      // time (model-viewer shares a single WebGL renderer — concurrent captures
      // bleed the same image), and caches each PNG for the rest of the page.
      const subjectList = modelPlan.map((m) => m.label);
      const modelImgs = await getModelImgsForSubjects(subjectList);
      if (cancelled) return;
      modelImgsRef.current = modelImgs;
      setCertHtml(makeHtml(modelImgs, false));
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWeb, modelPlan, name, dateStr, code]);

  // Render the exact cert HTML into an iframe (true WYSIWYG preview).
  useEffect(() => {
    if (!isWeb || !certHtml) return;
    const host = previewRef.current;
    if (!host) return;
    host.innerHTML = "";
    const iframe = document.createElement("iframe");
    iframe.title = "Certificate preview";
    iframe.style.width = "100%";
    iframe.style.height = "620px";
    iframe.style.border = "0";
    iframe.style.borderRadius = "14px";
    iframe.style.boxShadow = "0 18px 50px rgba(8,16,40,0.45)";
    iframe.srcdoc = certHtml;
    host.appendChild(iframe);
  }, [isWeb, certHtml]);

  useEffect(() => {
    if (!certHtml || typeof onHtmlReady !== "function") return;
    onHtmlReady(certHtml);
  }, [certHtml, onHtmlReady]);

  const handlePrint = () => {
    if (!isWeb) return;
    const html = makeHtml(modelImgsRef.current, true);
    const w = window.open("", "_blank", "width=1240,height=860");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // Non-web fallback (rare): minimal text certificate.
  if (!isWeb) {
    return (
      <View style={styles.fallback}>
        <Text style={styles.fbTitle}>Certificate of Completion</Text>
        <Text style={styles.fbName}>{name}</Text>
        <Text style={styles.fbDesc}>Three-Day Challenge · {dateStr}</Text>
        {onClose ? (
          <TouchableOpacity style={[styles.btn, styles.btnGhost]} onPress={onClose}>
            <Text style={styles.btnGhostText}>Done</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      {certHtml ? (
        <View nativeID="dv-cert-preview" ref={previewRef} style={styles.previewHost} />
      ) : (
        // Build phase: the model snapshots are captured off-DOM by the shared
        // util (davinciModels.js), so this is just a tasteful loading preview.
        <View style={styles.building}>
          <Text style={styles.buildingTitle}>Generating certificate</Text>
          <ActivityIndicator size="large" color="#c69a3a" />
          <Text style={styles.buildingText}>Rendering 3D models…</Text>
        </View>
      )}

      <View style={styles.actions}>
        <TouchableOpacity style={[styles.btn, styles.btnPrimary]} onPress={handlePrint} disabled={!certHtml}>
          <Text style={styles.btnPrimaryText}>🖨  Print Certificate</Text>
        </TouchableOpacity>
        {onClose ? (
          <TouchableOpacity style={[styles.btn, styles.btnGhost]} onPress={onClose}>
            <Text style={styles.btnGhostText}>Done</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { alignItems: "center", width: "100%", maxWidth: 980, alignSelf: "center" },
  previewHost: { width: "100%" },
  building: { width: "100%", minHeight: 360, alignItems: "center", justifyContent: "center", paddingVertical: 24 },
  buildingTitle: { fontSize: 16, fontWeight: "700", color: "#0b2a63", letterSpacing: 2, textTransform: "uppercase", marginBottom: 18 },
  buildingText: { marginTop: 14, fontSize: 13, color: "#64748b", letterSpacing: 1 },
  actions: { flexDirection: "row", gap: 12, marginTop: 22 },
  btn: { paddingVertical: 12, paddingHorizontal: 24, borderRadius: 10 },
  btnPrimary: { backgroundColor: "#0b2a63" },
  btnPrimaryText: { color: "#fff", fontWeight: "700", fontSize: 15 },
  btnGhost: { backgroundColor: "#e7ebf3" },
  btnGhostText: { color: "#1e293b", fontWeight: "700", fontSize: 15 },
  fallback: { alignItems: "center", padding: 30 },
  fbTitle: { fontSize: 22, fontWeight: "800", color: "#0b2a63" },
  fbName: { fontSize: 28, color: "#0b2a63", marginVertical: 8 },
  fbDesc: { fontSize: 14, color: "#475569", marginBottom: 16 },
});
